import { TextField } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import { useMutation, useNotify } from "ra-core";
import React from "react";
import { Delivery } from "../../../types";
import { convertIsoToLocale } from "../../../utils/date";
import { deliveryActions, deliveryStatus } from "../util";

interface Props {
  record: Delivery;
}

const VerifyDeliveryDialog: React.FC<Props> = (props) => {
  const { record: delivery } = props;
  const [open, setOpen] = React.useState(false);
  const [verificationNote, setVerificationNote] = React.useState("");
  const notify = useNotify();
  const verifyTime = new Date().toISOString();

  const [handleAccept] = useMutation(
    {
      type: "update",
      resource: "deliveries",
      payload: {
        id: delivery.id ?? "",
        data: {
          action: deliveryActions.VERIFY,
          verify_at: verifyTime,
          verification_note: verificationNote,
        },
      },
    },
    {
      onSuccess: () => {
        notify(`Successfully verified Delivery Order.`, "info");
        setOpen(false);
        setVerificationNote("");
      },
      onFailure: (error) => notify(`Error: ${error.message}`, "warning"),
    }
  );

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setVerificationNote("");
  };

  const handleVerificationNoteChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setVerificationNote(event.target.value);
  };

  // Only show verify button for delivered and unverified deliveries
  const canVerify =
    delivery.status === deliveryStatus.DELIVERED && !delivery.verify_at;

  if (!canVerify) {
    return null;
  }

  return (
    <>
      <Button color="primary" onClick={handleClickOpen} size={"small"}>
        Verify Delivery
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">{"Verify Delivery"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Please confirm to verify this delivery after checking the POD (Proof
            of Delivery).
            <br />
            <pre>
              Delivery ID: {delivery.id}
              <br />
              Product:{" "}
              {`${delivery.product_instance.product.name} (${delivery.product_instance.name})`}
              <br />
              Delivered At: {delivery.target_location.name}
              <br />
              Delivered Time:{" "}
              {delivery.delivered_time
                ? convertIsoToLocale(delivery.delivered_time)
                : "N/A"}
              <br />
              Driver: {delivery.driver?.full_name || "N/A"}
            </pre>
          </DialogContentText>

          <TextField
            margin="dense"
            id="verification-note"
            label="Verification Note (Optional)"
            placeholder="Add any verification notes or observations..."
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={verificationNote}
            onChange={handleVerificationNoteChange}
            style={{ marginTop: 16 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAccept} color="primary" autoFocus>
            Confirm Verification
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VerifyDeliveryDialog;
